import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import {
  BATTERY_COMPUTE_INVALID_CODE,
  BATTERY_COMPUTE_SUPERSEDED_CODE,
  batteryComputeErrorCode,
} from "~/api/battery-compute-errors"
import { queryKeys } from "~/api/cache"

import { invalidateBatteryPrice } from "./battery-price-dependents"
import type {
  BatteryProposalComputeDto,
  UpdateBatteryProposalComputeDto,
} from "../dto/battery-proposal-compute.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

export type UpdateBatteryProposalComputeVariables = {
  proposalId: string
  body: UpdateBatteryProposalComputeDto
}

/**
 * Saves the AI compute node, or none. The answer is the compute read itself.
 * An effective save moves the pricing-input generation, so the stored price
 * and everything derived from it report out of date until refreshed on
 * purpose; nothing recalculates. A failure refetches the read, since a lock
 * may be why it failed.
 */
export default function useUpdateBatteryProposalCompute(): UseMutationResult<
  BatteryProposalComputeDto,
  unknown,
  UpdateBatteryProposalComputeVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }: UpdateBatteryProposalComputeVariables) =>
      batteryProposalService.putCompute(proposalId, body),
    onSuccess: (saved, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.batteryProposal.compute(proposalId),
        saved,
      )
      // The render contract carries the node for the presentation, and the
      // pricing request now lists it.
      invalidateBatteryPrice(queryClient, proposalId)
      // The Finance Partner & Equipment save checks the node is still offered.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.inputs(proposalId),
      })
    },
    onError: (error, { proposalId }) => {
      // Superseded means the options were for an older equipment or Finance
      // Plan; invalid means PowurOS no longer offers the chosen node. Either
      // way the preview is stale, so it refetches with the read.
      const code = batteryComputeErrorCode(error)
      const optionsStale =
        code === BATTERY_COMPUTE_SUPERSEDED_CODE ||
        code === BATTERY_COMPUTE_INVALID_CODE
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.compute(proposalId),
        ...(optionsStale ? {} : { exact: true }),
      })
    },
  })
}
